import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Workflows from '../Workflows';
import OverflowChips from '../OverflowChips';
import { RC, rcScope } from '../../utils/fonts';
import { brandOr } from '../../utils/brand';

// SLF™ Visa / Company / License (owner 2026-09-23) — three desk consoles cut from SLF Property's
// Legal Matters: the Service Workflows engine narrowed to one desk (server-side), the desk's
// services grouped into family chips, "+ New Workflow" offering only that desk's services, and a
// matter's detail = the service's own flow template running (steps, SLF/CLIENT, uploads, deps,
// comments, client link, fee card). One row up top, exactly like SLF Property.
//
// Owner decisions (2026-09-23): the Jobs module is not touched — a desk matter is a workflow
// instance, not a job; matters opened here are MUTED (quiet) — the engine sends the client nothing,
// at creation or when steps unlock — until the owner turns notifications on; no Services tab, the
// chips and the wizard are how the catalogue is shown.
export type DeskKey = 'visa' | 'company' | 'license';

export default function ServiceDesk({ desk }: { desk: DeskKey }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The service families live in the title bar (owner 2026-09-25), the same bar SLF Property's
  // Legal Matters uses. Visa alone has ten of them, so they used to take the full width and wrap
  // onto a SECOND row, with the status chips and the search crushed into whatever was left — the
  // "乱七八糟" the owner saw. One row now: as many chips as fit, the rest folded into a dropdown at
  // the end that wears the selected one's name when the selection is inside it.
  //
  // Selection is held here and handed down, so Workflows draws no chip row of its own: one set on
  // screen, one piece of state behind it.
  const [fam, setFam] = useState('');
  const [families, setFamilies] = useState<{ key: string; label: string; priced: boolean; used: number }[]>([]);
  const chipBase: React.CSSProperties = { padding: '5px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 20, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)' };
  const chipOn: React.CSSProperties = { border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff' };
  const chips = [
    { key: '__all', label: t('wf_all'), active: !fam, onClick: () => setFam('') },
    // Only what this desk has ACTUALLY done (owner 2026-09-25). The bar is this office's book of
    // work, not the catalogue: a chip for a family with no matters could only ever return an empty
    // list. Every service is still offered when starting one — the + wizard lists the whole desk.
    // A family that is selected stays on the bar even if its last matter is deleted underneath you.
    ...families.filter(f => f.used > 0 || f.key === fam).map(f => ({
      key: f.key,
      // A family the price list has not caught up with keeps its chip, marked, rather than vanishing.
      label: f.priced ? f.label : `${f.label} ·`,
      active: fam === f.key,
      muted: !f.priced,
      title: f.priced ? undefined : t('lm_not_priced'),
      onClick: () => setFam(f.key),
    })),
  ];
  return (
    <div className="slf-desk" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {/* Typography follows Due Diligence across the console, as it does in SLF Property. */}
      <style>{rcScope('slf-desk')}</style>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', flexShrink: 0 }}>{t(`mod_slf_${desk}`)}</div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', flexShrink: 0, margin: '9px 0' }} aria-hidden />
        <OverflowChips chips={chips} chipStyle={chipBase} activeStyle={chipOn} mutedColor="var(--text-tertiary)" font={RC} />
      </header>
      <Workflows embedded desk={desk} quiet famCtl={{ fam, setFam }} onFamilies={setFamilies} />
    </div>
  );
}
